import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { SectionHeading } from "@/components/page";
import { ProgressBar } from "@/components/ui";
import type { Finance } from "@/lib/types";
import { isOverdue, Money, paymentStatus, Stat, StatusBadge, uncoveredExpenses, unfundedExpenses } from "./common";

export type FinanceTab = "overview" | "payments" | "expenses" | "team" | "rules";

export function OverviewTab({ fin, onOpen }: { fin: Finance; onOpen: (tab: FinanceTab) => void }) {
  const currency = fin.settings.currency;
  const money = (v: number) => <Money value={v} />;
  const inline = (v: number) => <Money value={v} layout="inline" />;
  const agreed = fin.settings.agreedAmount;
  const scheduled = fin.payments.reduce((n, p) => n + p.amount, 0);
  const received = fin.payments.reduce((n, p) => n + p.received, 0);
  const statuses = fin.payments.map((p) => ({ p, s: paymentStatus(p, currency) }));
  const overdue = fin.payments.filter((p) => isOverdue(p));
  const overdueTotal = overdue.reduce((n, p) => n + p.amount - p.received, 0);
  const expenses = fin.expenses.reduce((n, e) => n + e.amount, 0);
  const earned = fin.payments.reduce((n, p) => n + p.receipts.reduce((m, r) => m + r.distributed, 0), 0);
  const withdrawn = fin.payouts.reduce((n, o) => n + o.amount, 0);
  const held = fin.members.reduce((n, m) => n + m.cash, 0);
  const owedBetween = fin.debts.reduce((n, d) => n + (d.creditor !== null ? d.outstanding : 0), 0);
  const base = agreed || scheduled;
  const upcoming = statuses.filter((x) => x.s.key !== "paid").slice(0, 5);

  return (
    <div className="space-y-10">
      {agreed === 0 && (
        <button
          type="button"
          onClick={() => onOpen("rules")}
          className="flex w-full items-center justify-between gap-3 rounded-lg border border-dashed border-line px-4 py-3 text-start text-sm hover:bg-hover"
        >
          <span>
            <span className="font-medium">Set the contract value</span>
            <span className="text-muted"> to track how much of the project has been paid.</span>
          </span>
          <ChevronRightIcon className="size-4 shrink-0 text-faint rtl:rotate-180" />
        </button>
      )}

      <section className="space-y-3">
        <SectionHeading>Client payments</SectionHeading>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label="Contract value" value={agreed ? money(agreed) : "—"} hint={agreed ? <>{inline(scheduled)} scheduled</> : "Not set"} tone={agreed ? undefined : "muted"} />
          <Stat label="Received" value={money(received)} hint={base ? <ProgressBar done={received} total={base} className="mt-1.5" /> : "Nothing scheduled yet"} />
          <Stat label="Still to receive" value={money(Math.max(0, base - received))} hint={agreed ? "Contract minus received" : "Scheduled minus received"} />
          <Stat
            label="Overdue"
            value={money(overdueTotal)}
            hint={overdue.length ? `${overdue.length} ${overdue.length === 1 ? "payment" : "payments"} past due` : "Nothing overdue"}
            tone={overdueTotal > 0 ? "danger" : "muted"}
          />
        </div>
      </section>

      <section className="space-y-3">
        <SectionHeading>Costs and team</SectionHeading>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label="Expenses" value={money(expenses)} hint={`${fin.expenses.length} recorded`} />
          <Stat
            label="Expenses not covered yet"
            value={money(uncoveredExpenses(fin) + unfundedExpenses(fin))}
            hint={owedBetween > 0 ? <>{inline(owedBetween)} also owed between members</> : "Repaid from members' next payments"}
          />
          <Stat label="Earned by the team" value={money(earned)} hint="Locked as money is received" />
          <Stat label="Held for the team" value={money(held)} hint={<>{inline(withdrawn)} withdrawn</>} />
        </div>
      </section>

      <section>
        <SectionHeading
          right={
            <button type="button" onClick={() => onOpen("payments")} className="text-[13px] text-muted hover:text-fg">
              All payments
            </button>
          }
        >
          Next payments
        </SectionHeading>
        {upcoming.length === 0 ? (
          <p className="text-sm text-muted">{fin.payments.length ? "Every scheduled payment has been received." : "No payments scheduled yet."}</p>
        ) : (
          <div className="divide-y divide-line rounded-lg border border-line">
            {upcoming.map(({ p, s }) => (
              <div key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
                <span className="min-w-0 flex-1 truncate font-medium" dir="auto">
                  {p.title}
                </span>
                <span className="text-xs text-muted">{s.detail}</span>
                <StatusBadge status={s} />
                <span className="w-40 text-end font-medium">{money(p.amount - p.received)}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
